import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, MessageCircle, Instagram, ArrowUp } from 'lucide-react';
import { useSiteContent } from '@/lib/useSiteContent';
import { useAuth } from '@/lib/AuthContext';
import { Image } from '@/components/ui/image';

const features = [
  { icon: Truck, label: 'Sendum út um allt land' },
  { icon: MessageCircle, label: 'Persónuleg ráðgjöf' },
];

const cols = [
  {
    title: 'Úrvalið',
    links: [
      { label: 'Landslið', to: '/safnid?category=Landslið' },
      { label: 'Félagslið', to: '/safnid?category=Félagslið' },
    ],
  },
  {
    title: 'Upplýsingar',
    links: [
      { label: 'Treyja vikunnar', to: '/#treyja-vikunnar' },
      { label: 'Selja með okkur', to: '/seldu' },
      { label: 'Óska eftir treyju', to: '/draumatreyja' },
      { label: 'Heimsókn', to: '/heimsokn' },
      { label: 'Staða gjafabréfs', to: '/gjafabref/stada' },
      { label: 'Algengar spurningar', to: '/spurningar' },
      { label: 'Um okkur', to: '/um-okkur' },
      { label: 'Skilmálar, skil og afhending', to: '/skilmalar' },
      { label: 'Persónuvernd', to: '/personuvernd' },
    ],
  },
];

export default function Footer() {
  const { content } = useSiteContent();
  const { user } = useAuth();
  const f = content.footer;
  const isAdmin = user?.role === 'admin';

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="border-b border-primary-foreground/15">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16">
          {features.map((feat) => (
            <div key={feat.label} className="flex items-center gap-3">
              <feat.icon className="w-5 h-5" strokeWidth={1.25} />
              <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{feat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-4">
            <Image src="https://media.base44.com/images/public/6aa2c2af54cfb5bf4d045125/41cbee118_Untitleddesign10.png" alt="Vintage Treyjur" width="48" height="48" loading="lazy" fittingType="fit" className="w-12 h-12 object-contain flex-shrink-0" />
            <div className="leading-none">
              <p className="font-heading text-base">Vintage Treyjur</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70 mt-0.5">Reykjavík · Est. 2026</p>
            </div>
          </div>
          <p className="font-body text-sm opacity-75 leading-relaxed max-w-xs">{f.description}</p>
          <div className="flex gap-3 mt-6">
            <a href="https://www.instagram.com/vintagetreyjur" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full border border-primary-foreground/40 flex items-center justify-center hover:bg-primary-foreground/10 transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=61594177888752" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full border border-primary-foreground/40 flex items-center justify-center hover:bg-primary-foreground/10 transition-colors font-sans font-bold text-sm">
              f
            </a>
          </div>
        </div>

        {cols.map((c) => (
          <div key={c.title} className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">{c.title}</p>
            <ul className="space-y-2">
              {c.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="font-body text-sm opacity-75 hover:opacity-100 hover:text-accent transition-colors inline-block">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="md:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold mb-4">Samband</p>
          <ul className="space-y-2">
            <li className="font-body text-sm opacity-75">{f.address}</li>
            <li>
              <a href={`tel:${f.phone.replace(/\s/g, '')}`} className="font-body text-sm opacity-75 hover:opacity-100 hover:text-accent transition-colors">{f.phone}</a>
            </li>
            <li>
              <a href={`mailto:${f.email}`} className="font-body text-sm opacity-75 hover:opacity-100 hover:text-accent transition-colors break-all">{f.email}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-primary-foreground/15">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 flex flex-col md:flex-row justify-between gap-2 font-mono text-[9px] uppercase tracking-[0.2em] opacity-60">
          <span>© 2026 Vintage Treyjur — Allur réttur áskilinn</span>
          <div className="flex gap-4 items-center">
            {isAdmin && (
              <>
                <Link to="/stjornbord" className="hover:opacity-100 hover:text-accent">Stjórnborð</Link>
                <Link to="/reikningur" className="hover:opacity-100 hover:text-accent">Reikningur</Link>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="bg-muted">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-primary">
          <span className="hidden md:block">{f.address}</span>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-accent inline-flex items-center gap-1">
            <ArrowUp className="w-3 h-3" /> Efst
          </button>
        </div>
      </div>
    </footer>
  );
}